import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import moment from 'moment';

// actions
import { fetchPaymentMethodsPerCountry } from 'ducks/merchantDucks/payments/actions';

// selectors
import { getPaymentDetailsPerCountry, getTotalPaymentsPerCountry } from 'ducks/merchantDucks/payments/selectors';

// types
import { HistogramData } from 'ducks/merchantDucks/reports/types';
import { PerMethodType, CountryPerMethod } from 'ducks/merchantDucks/payments/types';

// utils
import isEmpty from 'lodash/isEmpty';
import values from 'lodash/values';
import mapValues from 'lodash/mapValues';
import keyBy from 'lodash/keyBy';
import { convertCountryNamesToGeoJSON } from 'utils/convertCountryCustomersToGeoJSON';

// components
import Loader from 'components/Loader';
import EmptyMessage from 'components/EmptyMessage';
import PieChart from 'pages/MerchantPages/Analytics/components/charts/PieChart';
import Tile from 'pages/MerchantPages/Analytics/components/Tile';
import customLegend from 'components/Legend/CustomLegend';
import { CustomMap } from 'components/StyledMap';
import Choropleth from 'components/Leaflet/Choropleth';
import { Button } from '@inplayer-org/inplayer-ui';
import TopCountiesGeneralComponent from './TopCountriesGeneralComponent';
import { convertCountryCodeToLatLng } from './convertCountryCodeToLatLng';

export const Wrapper = styled.div<{ isHeight?: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 1.6rem;
  height: ${({ isHeight }) => isHeight && '300px'};
`;

const BackButton = styled(Button)`
  width: 11rem;
  align-self: flex-end;
`;

interface Props {
  startDate: string;
  endDate: string;
  isFetching: boolean;
  countrySalesCollection?: HistogramData[];
}

const getNCountries = (
  numberOfCountries: number,
  data?: CountryPerMethod,
  includeOthers?: boolean
): CountryPerMethod => {
  const countriesArray = values(
    mapValues(data, (value, key) => ({
      ...value,
      name: key,
    }))
  );
  const sortedCountries = countriesArray.sort((first, second) => second.total - first.total);
  const firstN = sortedCountries.slice(0, numberOfCountries);
  const finalCountries = [...firstN];
  if (includeOthers) {
    const otherCountries: any = { total: 0, name: 'Others' };
    for (let index = numberOfCountries; index < sortedCountries.length; index += 1) {
      const element = sortedCountries[index];
      otherCountries.total += element.total;
    }
    if (otherCountries.total) {
      finalCountries.push(otherCountries);
    }
  }

  return keyBy(finalCountries, (o) => {
    return o.name;
  });
};

const formatCollection = (data?: CountryPerMethod | PerMethodType) => {
  if (!data) return [];

  return Object.keys(data).map((key) => ({
    key,
    value: data[key].total,
  }));
};

const PaymentTiles: React.FC<Props> = ({ startDate, endDate, countrySalesCollection = [], isFetching }) => {
  const dispatch = useDispatch();
  const paymentDetailsPerCountry = useSelector(getPaymentDetailsPerCountry);
  const totalPayments = useSelector(getTotalPaymentsPerCountry);
  const geojson = convertCountryNamesToGeoJSON(paymentDetailsPerCountry ?? {});

  const [mapCountryCode, setMapCountryCode] = useState('') as any;
  const [isTopCountriesView, setIsTopCountriesView] = useState(true);
  const [currentCountryName, setCurrentCountryName] = useState('');
  const [centerCoordinates, setCenterCoordinates] = useState([
    [0, 0],
    [0, 0],
  ]);

  const getBacktoTopCountries = () => {
    setIsTopCountriesView(true);
    setCurrentCountryName('');
    setCenterCoordinates([
      [0, 0],
      [0, 0],
    ]);
  };

  const setCurrentCountryForPaymentMethods = (countryCode: string) => {
    setCurrentCountryName(countryCode);
    setIsTopCountriesView(false);
    const cords = convertCountryCodeToLatLng(countryCode);
    setCenterCoordinates(cords);
  };

  useEffect(() => {
    // reset the map to default view
    getBacktoTopCountries();
    dispatch(fetchPaymentMethodsPerCountry(startDate, endDate));
  }, [fetchPaymentMethodsPerCountry, startDate, endDate]);

  useEffect(() => {
    if (mapCountryCode) setCurrentCountryForPaymentMethods(mapCountryCode);
  }, [mapCountryCode]);

  const top20Countries = useMemo(() => {
    return getNCountries(20, paymentDetailsPerCountry);
  }, [paymentDetailsPerCountry]);
  const top5CountriesWithOthers = useMemo(() => {
    return getNCountries(5, paymentDetailsPerCountry, true);
  }, [paymentDetailsPerCountry]);

  const PaymentsMap = () => (
    <CustomMap center={[50, 0]} zoom={1} minZoom={1} maxBoundsViscosity={1} attributionControl={false}>
      <Choropleth
        countryCode={setMapCountryCode}
        coordinates={centerCoordinates}
        geoJson={geojson}
        featureProp="doc_count"
        mapDescriptionType="Payment"
      />
    </CustomMap>
  );

  const pieChartData = useMemo(() => {
    return formatCollection(
      isTopCountriesView
        ? top5CountriesWithOthers
        : paymentDetailsPerCountry && paymentDetailsPerCountry[currentCountryName]?.per_method
    );
  }, [paymentDetailsPerCountry, isTopCountriesView]);

  if (isFetching) {
    return <Loader />;
  }

  return (
    <>
      {!isTopCountriesView && (
        <BackButton buttonModifiers={['buttonLink']} onClick={getBacktoTopCountries}>
          back to world view
        </BackButton>
      )}
      <Wrapper>
        <Tile
          title="Payments by country"
          subtitle={`${moment(startDate).format('DD-MM-YYYY')} to ${moment(endDate).format('DD-MM-YYYY')}`}
          tileWidth="100%"
          legend={!isEmpty(paymentDetailsPerCountry) && customLegend('key', '', 'doc_count', countrySalesCollection)}
          tileContent={
            <>
              {!isEmpty(paymentDetailsPerCountry) ? (
                <PaymentsMap />
              ) : (
                <EmptyMessage variant="p">There is no available data for this time period.</EmptyMessage>
              )}
            </>
          }
        />
        <Tile
          title={isTopCountriesView ? 'Top countries' : `Payments for ${currentCountryName} per method`}
          subtitle={`${moment(startDate).format('DD-MM-YYYY')} to ${moment(endDate).format('DD-MM-YYYY')}`}
          tileWidth="100%"
          isBorder
          tileContent={
            <>
              {!isEmpty(paymentDetailsPerCountry) ? (
                <Wrapper>
                  <TopCountiesGeneralComponent
                    isTopCountriesView={isTopCountriesView}
                    paymentDetailsPerCountry={top20Countries}
                    currentCountry={currentCountryName}
                    setCurrentCountryForPaymentMethods={setCurrentCountryForPaymentMethods}
                    totalPayments={totalPayments}
                  />
                  <PieChart
                    data={pieChartData}
                    totalItems={isTopCountriesView ? totalPayments : top20Countries[currentCountryName]?.total}
                  />
                </Wrapper>
              ) : (
                <EmptyMessage variant="p">There is no available data for this time period.</EmptyMessage>
              )}
            </>
          }
        />
      </Wrapper>
    </>
  );
};

export default PaymentTiles;
